import { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logger from '../utils/logger';

function parseDeadlineMs(deadline) {
  if (!deadline) return null;
  const ts = Date.parse(deadline);
  return Number.isNaN(ts) ? null : ts;
}

/**
 * Enforces global screening expiry across public screening pages.
 *
 * - Redirects to `/apply/:screeningId/time-up` when the deadline passes.
 * - Also checks server-provided `timeUp` (force-complete) via periodic polling.
 * - Does not render UI; safe to use on pages where timer must be hidden.
 */
export function useScreeningExpiryRedirect({
  screeningId,
  email,
  enabled = true,
  pollIntervalMs = 30_000,
} = {}) {
  const navigate = useNavigate();

  const emailNorm = useMemo(() => {
    const e = typeof email === 'string' ? email.trim().toLowerCase() : '';
    return e || '';
  }, [email]);

  const timeoutRef = useRef(null);
  const pollRef = useRef(null);
  const latestRedirectedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !screeningId) return undefined;
    latestRedirectedRef.current = false;

    const clearTimers = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const redirect = () => {
      if (latestRedirectedRef.current) return;
      latestRedirectedRef.current = true;
      navigate(`/apply/${screeningId}/time-up`, { replace: true });
    };

    const scheduleLocalDeadlineRedirect = (deadline) => {
      const deadlineMs = parseDeadlineMs(deadline);
      if (!deadlineMs) return;
      const delay = Math.max(0, deadlineMs - Date.now());
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        redirect();
      }, delay);
    };

    const fetchApplyInfoAndGate = async () => {
      try {
        const query = emailNorm ? `?email=${encodeURIComponent(emailNorm)}` : '';
        const res = await axios.get(`/api/screenings/${screeningId}/apply-info${query}`);
        const payload = res.data || {};
        const deadline = payload.screeningDeadline || null;
        const timeUp = !!payload.timeUp;

        if (timeUp) {
          redirect();
          return;
        }

        const deadlineMs = parseDeadlineMs(deadline);
        if (deadlineMs != null && Date.now() >= deadlineMs) {
          redirect();
          return;
        }

        scheduleLocalDeadlineRedirect(deadline);
      } catch (err) {
        // Best-effort enforcement; do not block user on transient network errors.
        logger.warn?.('[useScreeningExpiryRedirect] Failed to load apply-info', err);
      }
    };

    // Initial fetch + schedule.
    fetchApplyInfoAndGate();

    // Only poll when an email is known (i.e. screening has started and there's a
    // deadline to track). Without email the response never includes a deadline,
    // so repeated polling is wasted work.
    if (emailNorm) {
      pollRef.current = setInterval(() => {
        fetchApplyInfoAndGate();
      }, Math.max(5_000, pollIntervalMs));
    }

    return () => clearTimers();
  }, [enabled, screeningId, emailNorm, pollIntervalMs, navigate]);
}

